import React, { useEffect, useState } from 'react';
import { Modal, Paper, SegmentedControl, Stack, Table, Text } from '@mantine/core';

interface TokenSolInvestExampleModalProps {
  opened: boolean;
  onClose: () => void;
  minSolTokenInvested?: number | null;
}

type PreviewMode = 'all' | '0.05' | '0.1';

interface ExampleToken {
  address: string;
  investedSol: number;
  result: 'Win' | 'Loss';
}

const EXAMPLE_TOKENS: ExampleToken[] = [
  { address: 'Aj3…pump', investedSol: 0.02, result: 'Loss' },
  { address: '9Ks…pump', investedSol: 0.05, result: 'Win' },
  { address: 'F4p…pump', investedSol: 0.08, result: 'Loss' },
  { address: 'Dq8…pump', investedSol: 0.10, result: 'Win' },
  { address: 'K7v…pump', investedSol: 0.25, result: 'Win' },
];

const getInitialMode = (value?: number | null): PreviewMode => {
  if (value === 50_000_000) return '0.05';
  if (value === 100_000_000) return '0.1';
  return 'all';
};

const TokenSolInvestExampleModal: React.FC<TokenSolInvestExampleModalProps> = ({
  opened,
  onClose,
  minSolTokenInvested,
}) => {
  const [previewMode, setPreviewMode] = useState<PreviewMode>(() => getInitialMode(minSolTokenInvested));

  useEffect(() => {
    if (opened) setPreviewMode(getInitialMode(minSolTokenInvested));
  }, [opened, minSolTokenInvested]);

  const threshold = previewMode === 'all' ? null : Number(previewMode);
  const includedTokens = EXAMPLE_TOKENS.filter(token =>
    threshold == null || token.investedSol >= threshold
  );
  const allWins = EXAMPLE_TOKENS.filter(token => token.result === 'Win').length;
  const includedWins = includedTokens.filter(token => token.result === 'Win').length;
  const allWinrate = allWins / EXAMPLE_TOKENS.length * 100;
  const filteredWinrate = includedWins / includedTokens.length * 100;
  const winrateChange = filteredWinrate - allWinrate;
  const filterLabel = previewMode === 'all' ? 'ALL' : `≥ ${previewMode} SOL`;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Token SOL Invest — example"
      centered
      size="md"
      overlayProps={{ opacity: 0.65, blur: 4 }}
      styles={{
        content: { backgroundColor: '#0D1117', border: '1px solid #374151' },
        header: { backgroundColor: '#0D1117', borderBottom: '1px solid #21262D' },
        title: { color: '#F9FAFB', fontWeight: 700, fontSize: 18 },
        close: { color: '#9CA3AF' },
        body: { padding: 20 },
      }}
    >
      <Stack gap="md">
        <Text size="sm" c="#CBD5E1" lh={1.5}>
          Pick a threshold to see which tokens remain in the wallet calculation.
        </Text>

        <SegmentedControl
          fullWidth
          size="xs"
          value={previewMode}
          onChange={value => setPreviewMode(value as PreviewMode)}
          data={[
            { value: 'all', label: 'All' },
            { value: '0.05', label: '≥ 0.05 SOL' },
            { value: '0.1', label: '≥ 0.1 SOL' },
          ]}
          styles={{
            root: { backgroundColor: '#161B22', border: '1px solid #374151' },
            indicator: { backgroundColor: '#4F46E5' },
            label: { color: '#D1D5DB', fontWeight: 600 },
          }}
        />

        <Paper
          radius="md"
          style={{ overflow: 'hidden', backgroundColor: '#07090E', border: '1px solid #2D3748' }}
        >
          <Table
            horizontalSpacing="md"
            verticalSpacing={10}
            withRowBorders
            styles={{
              th: { color: '#6B7280', fontSize: 10, fontWeight: 600, borderColor: '#21262D' },
              td: { borderColor: '#21262D' },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Token</Table.Th>
                <Table.Th ta="right">Invested</Table.Th>
                <Table.Th ta="right">Result</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {EXAMPLE_TOKENS.map(token => {
                const included = threshold == null || token.investedSol >= threshold;
                const isWin = token.result === 'Win';

                return (
                  <Table.Tr
                    key={token.address}
                    style={{
                      opacity: included ? 1 : 0.2,
                      backgroundColor: included ? 'rgba(99, 102, 241, 0.035)' : 'transparent',
                      transition: 'opacity 180ms ease, background-color 180ms ease',
                    }}
                  >
                    <Table.Td>
                      <Text size="xs" ff="monospace" c="#E5E7EB">{token.address}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="xs" c="#B6BECA">{token.investedSol.toFixed(2)} SOL</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="xs" fw={600} c={isWin ? '#6EE7B7' : '#FCA5A5'}>
                        {token.result}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>

        <Text size="10px" c="#6B7280" ta="center">
          Bright tokens are counted · dimmed tokens are ignored
        </Text>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 38px minmax(0, 1fr)',
          alignItems: 'stretch',
          gap: 7,
        }}>
          <Paper
            p="sm"
            radius="md"
            style={{ backgroundColor: '#161B22', border: '1px solid #374151', textAlign: 'center' }}
          >
            <Text size="10px" fw={700} c="#9CA3AF" mb={4}>ALL</Text>
            <Text size="xs" c="#9CA3AF">{allWins} wins / {EXAMPLE_TOKENS.length} tokens</Text>
            <Text size="24px" fw={800} c="#E5E7EB" lh={1.15}>{allWinrate.toFixed(0)}%</Text>
            <Text size="9px" c="#6B7280">WINRATE</Text>
          </Paper>

          <Stack align="center" justify="center" gap={2}>
            <Text size="22px" c="#6366F1">→</Text>
            <Text size="9px" fw={700} c={winrateChange > 0 ? '#6EE7B7' : '#6B7280'}>
              {winrateChange > 0 ? '+' : ''}{winrateChange.toFixed(0)} pp
            </Text>
          </Stack>

          <Paper
            p="sm"
            radius="md"
            style={{
              background: previewMode === 'all'
                ? '#161B22'
                : 'linear-gradient(135deg, rgba(79, 70, 229, 0.2), rgba(16, 185, 129, 0.08))',
              border: previewMode === 'all' ? '1px solid #374151' : '1px solid rgba(99, 102, 241, 0.7)',
              textAlign: 'center',
              transition: 'background 180ms ease, border-color 180ms ease',
            }}
          >
            <Text size="10px" fw={700} c="#A5B4FC" mb={4}>{filterLabel}</Text>
            <Text size="xs" c="#9CA3AF">{includedWins} wins / {includedTokens.length} tokens</Text>
            <Text size="24px" fw={800} c="#F9FAFB" lh={1.15}>{filteredWinrate.toFixed(0)}%</Text>
            <Text size="9px" c="#818CF8">WINRATE</Text>
          </Paper>
        </div>

        <Paper
          px="sm"
          py={8}
          radius="sm"
          style={{ backgroundColor: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.2)' }}
        >
          <Text size="xs" c="#AEB6C4" ta="center" lh={1.45}>
            ROI, median ROI, PnL and the other wallet metrics are recalculated from the same bright rows.
          </Text>
        </Paper>
      </Stack>
    </Modal>
  );
};

export default TokenSolInvestExampleModal;
